// `cn` — the class merger every shadcn component imports.
//
// Two jobs, and they are different: `clsx` flattens conditional class arguments into a
// string, and `tailwind-merge` resolves CONFLICTS within that string by Tailwind's own
// rules — so `cn("p-2", "p-4")` is `p-4` rather than both, and a caller's `className`
// can actually override a component's default instead of losing to source order.
//
// Without the merge, every override in this codebase would depend on which class the
// bundler happened to emit first, which is not a thing anybody can reason about.

import { type ClassValue, clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * RIKSU'S TYPE SCALE, DECLARED TO THE MERGER.
 *
 * `text-*` is two Tailwind namespaces wearing one prefix: a font SIZE (`text-sm`) and a
 * COLOUR (`text-red-500`). tailwind-merge tells them apart by recognising the sizes —
 * its own t-shirt scale — and filing everything else under colour. riksu's scale is
 * neither, so all eight of these were classified as COLOURS.
 *
 * The consequence is that a size and a colour looked like the same conflict, and the
 * merger dropped one. Measured before this list existed, on all 32 pairs of riksu's
 * eight sizes against four colours:
 *
 *     cn("text-shell-body", "text-muted-foreground")  ->  "text-muted-foreground"
 *     cn("text-muted-foreground", "text-shell-body")  ->  "text-shell-body"
 *
 * **32 of 32 lost a class**, in whichever order they arrived — so the surviving one was
 * decided by source order, which is the thing `cn` exists to stop. The control proves it
 * is these names and not the mechanism: `cn("text-sm", "text-muted-foreground")` keeps
 * both, because tailwind-merge knows `text-sm` is a size.
 *
 * Two consequences worth naming, because both are silent:
 *
 * - A component's own size lost to a caller's colour, so the element rendered at the
 *   INHERITED size rather than the one the code names.
 * - Two riksu sizes in one `cn` still resolved correctly — later wins — because they
 *   were in one group. That accident is why the whole class looked fine.
 *
 * `text-` here is the prefix tailwind-merge strips, so these are the names WITHOUT it.
 * A ninth type role added to `design/tokens/` and not added here is caught by
 * `utils.test.ts`, which reads the generated stylesheet rather than trusting this list.
 */
const TYPE_SCALE = [
  "auth-title",
  "data-cell",
  "data-label",
  "data-tag",
  "shell-body",
  "shell-caption",
  "shell-label",
  "shell-surface-title",
  "shell-title",
];

const twMerge = extendTailwindMerge({
  extend: { classGroups: { "font-size": [{ text: TYPE_SCALE }] } },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** The type-scale names `cn` knows about. Exported for the test that pins them. */
export const TYPE_SCALE_UTILITIES = TYPE_SCALE.map((role) => `text-${role}`);
